import { useEffect, useState } from 'react';
import type { InstalledExtension } from '../types';
import { deleteExtension, listExtensions, putExtension } from '../lib/db';
import { parseExtensionPackage } from '../lib/extensions';
import { uid } from '../lib/ids';

interface Props {
  extensions: InstalledExtension[];
  onChange: (extensions: InstalledExtension[]) => void;
}

const SAMPLE = JSON.stringify({
  manifest: {
    manifest_version: 3,
    name: 'Highlight Links',
    version: '1.0.0',
    description: 'Demo MV3-style content script.',
    content_scripts: [{ matches: ['<all_urls>'], js: ['content.js'], run_at: 'document_idle' }]
  },
  scripts: {
    'content.js': "document.querySelectorAll('a').forEach(a => { a.style.outline = '1px dashed #60a5fa'; }); console.info('Highlight Links extension ran');"
  }
}, null, 2);

export function ExtensionsPage({ extensions, onChange }: Props) {
  const [source, setSource] = useState(SAMPLE);
  const [message, setMessage] = useState('');

  useEffect(() => { listExtensions().then(onChange); }, [onChange]);

  async function install() {
    try {
      const parsed = parseExtensionPackage(source);
      const installed: InstalledExtension = { id: uid('ext'), manifest: parsed.manifest, scripts: parsed.scripts, enabled: true, installedAt: Date.now() };
      await putExtension(installed);
      const next = await listExtensions();
      onChange(next);
      setMessage(`${installed.manifest.name} installed.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Install failed.');
    }
  }

  async function toggle(extension: InstalledExtension) {
    await putExtension({ ...extension, enabled: !extension.enabled });
    onChange(await listExtensions());
  }

  async function remove(extension: InstalledExtension) {
    await deleteExtension(extension.id);
    onChange(await listExtensions());
  }

  return (
    <section className="internal-page">
      <header className="page-header"><div><p className="eyebrow">websurf://extensions</p><h1>Extensions</h1></div><span>Simulated Chrome MV3 content-script compatibility.</span></header>
      <div className="manager-grid">
        <aside className="editor-card code-card">
          <h2>Install package JSON</h2>
          <textarea value={source} onChange={(event) => setSource(event.target.value)} spellCheck={false} />
          <button onClick={install}>Install extension</button>
          {message && <p className="helper-copy">{message}</p>}
        </aside>
        <div className="list-card">
          {extensions.map((extension) => (
            <div className="list-row" key={extension.id}>
              <div className="row-main"><strong>{extension.manifest.name}</strong><span>{extension.manifest.description || extension.manifest.version}</span></div>
              <button onClick={() => toggle(extension)}>{extension.enabled ? 'Disable' : 'Enable'}</button>
              <button onClick={() => remove(extension)}>Remove</button>
            </div>
          ))}
          {extensions.length === 0 && <p className="empty-copy">No extensions installed.</p>}
        </div>
      </div>
    </section>
  );
}
